import type { ApplicationConfig } from '@angular/core';
import type { CredentialsFetcher } from '@wizbii/algolia';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, importProvidersFrom, inject, LOCALE_ID, provideEnvironmentInitializer } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { bugsnagConfig } from '@config/bugsnag.config';
import { AlgoliaCredentialFetcherService } from '@core/services/algolia/algolia-credential-fetcher.service';
import { DialogService } from '@core/services/dialog/dialog.service';
import { JwtService } from '@core/services/jwt.service';
import { WizbiiRouterStateSerializer } from '@core/services/router-state-serializer.service';
import { environment } from '@environment';
import { FEATURES_ROUTES } from '@features/features.routes';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { RouterStateSerializer, withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { provideStore, Store } from '@ngxs/store';
import { LocationWebservice } from '@webservices/location-api/location.webservice';
import { SuggestionWebservice } from '@webservices/suggestion-api/suggestion.webservice';
import {
  FILE_PICKER_SERVICE_TOKEN,
  FileWebService,
  MUST_BE_AUTHORIZED_GUARD_CONFIG,
  PageUnauthorizedModule,
} from '@wizbii-components/bo-angular-ui';
import { bugsnagErrorHandlerFactory } from '@wizbii-utils/angular/bugsnag';
import { ImageService } from '@wizbii-utils/angular/core';
import { JWT_SERVICE_TOKEN, JwtInterceptor } from '@wizbii-utils/angular/jwt';
import { JWT_STATE_CONFIG, JwtState, JwtStateModule } from '@wizbii-utils/angular/stores';
import {
  AUTHENTICATION_API_CONFIG,
  AuthenticationWebservice,
  CityWebservice,
  FILE_API_CONFIG,
  FileWebservice,
  PLACE_API_CONFIG,
} from '@wizbii-utils/angular/webservices';
import { ALGOLIA_CREDENTIALS_FETCHERS_TOKEN, AlgoliaClientProvider, AlgoliaModule } from '@wizbii/algolia';
import { QuicklinkStrategy } from 'ngx-quicklink';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      FEATURES_ROUTES,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'always' }),
      withPreloading(QuicklinkStrategy)
    ),
    provideAnimations(),
    provideStore(
      [],
      { developmentMode: !environment.production },
      withNgxsRouterPlugin(),
      withNgxsFormPlugin(),
      withNgxsReduxDevtoolsPlugin({ name: 'Wizbii Suggestii', maxAge: 30 })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(JwtStateModule, AlgoliaModule, MatDialogModule, PageUnauthorizedModule),
    {
      provide: JWT_STATE_CONFIG,
      useValue: {
        jwtCookieName: 'wizbii_bo',
        expiryCookieName: 'wizbii_bo_expiry',
        appEnvFqdn: environment.domain.cookieDomain,
      },
    },
    DialogService,
    SuggestionWebservice,
    { provide: ErrorHandler, useFactory: bugsnagErrorHandlerFactory(bugsnagConfig) },
    { provide: RouterStateSerializer, useClass: WizbiiRouterStateSerializer },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: JWT_SERVICE_TOKEN, useClass: JwtService },
    { provide: LOCALE_ID, useValue: 'fr' },
    { provide: ImageService, useValue: new ImageService(environment.api.imaginary) },
    AlgoliaCredentialFetcherService,
    {
      provide: ALGOLIA_CREDENTIALS_FETCHERS_TOKEN,
      useFactory: (fetcher: CredentialsFetcher) => ({ main: fetcher }),
      deps: [AlgoliaCredentialFetcherService],
    },
    AlgoliaClientProvider,
    LocationWebservice,
    { provide: PLACE_API_CONFIG, useValue: { baseUrl: environment.api.place } },
    CityWebservice,
    {
      provide: FILE_API_CONFIG,
      useValue: {
        baseUrl: `https://file.${environment.apiDomain}`,
        googleStorageUrl: 'https://storage.googleapis.com',
        wizbiiFilesBucket: environment.wizbiiFilesBucket,
      },
    },
    {
      provide: MUST_BE_AUTHORIZED_GUARD_CONFIG,
      useFactory: (store: Store) => ({
        listRoles$: store.select(JwtState.roles),
        listAuthorizedRoles: ['ROLE_SUGGESTII_MANAGER'],
        platform: environment.platform,
      }),
      deps: [Store],
    },
    FileWebservice,
    { provide: FILE_PICKER_SERVICE_TOKEN, useClass: FileWebService },
    {
      provide: AUTHENTICATION_API_CONFIG,
      useValue: { appId: environment.applicationId, baseUrl: environment.api.authentication },
    },
    AuthenticationWebservice,
    provideEnvironmentInitializer(() => {
      inject(MatIconRegistry).addSvgIconSet(
        inject(DomSanitizer).bypassSecurityTrustResourceUrl(`${environment.deployAssetsUrl ?? '/assets'}/sprite.svg`)
      );
    }),
  ],
};
